import { type ClassValue, clsx } from "clsx"
import { format, formatDistanceStrict, isValid, parseISO } from "date-fns"
import { twMerge } from "tailwind-merge"

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

/**
 * Formats a number as GBP currency.
 * Returns "N/A" if the value is null.
 */
export function formatCurrency(value: number | null, currency = "GBP"): string {
  if (value === null) return "N/A"
  return new Intl.NumberFormat("en-GB", {
    style: "currency",
    currency,
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value)
}

/**
 * Formats a number as compact GBP (e.g., "£109M", "£1.6M", "£991K").
 * Returns "N/A" if the value is null.
 */
export function formatCurrencyCompact(value: number | null): string {
  if (value === null) return "N/A"
  return new Intl.NumberFormat("en-GB", {
    style: "currency",
    currency: "GBP",
    notation: "compact",
    maximumSignificantDigits: 3,
  }).format(value)
}

/**
 * Safely formats a date string to the specified format.
 * Returns a fallback value if the date is invalid or null.
 *
 * @param dateString - The date string to format (ISO 8601 format expected)
 * @param formatStr - The date-fns format string (default: "dd MMM yyyy")
 * @param fallback - The fallback value if date is invalid (default: "N/A")
 * @returns The formatted date string or fallback value
 */
export function safeFormatDate(
  dateString: string | null | undefined,
  formatStr = "dd MMM yyyy",
  fallback = "N/A",
): string {
  if (!dateString) {
    return fallback
  }

  try {
    const date = parseISO(dateString)
    if (!isValid(date)) {
      return fallback
    }
    return format(date, formatStr)
  } catch {
    return fallback
  }
}

/**
 * Safely parses an ISO date string to a Date object.
 * Returns null if the date is invalid.
 *
 * @param dateString - The date string to parse (ISO 8601 format expected)
 * @returns The Date object or null if invalid
 */
export function safeParseDateISO(
  dateString: string | null | undefined,
): Date | null {
  if (!dateString) {
    return null
  }

  try {
    const date = parseISO(dateString)
    return isValid(date) ? date : null
  } catch {
    return null
  }
}

/**
 * Formats the calendar date portion of an ISO timestamp without shifting by
 * the local runtime timezone.
 */
export function formatIsoCalendarDate(
  dateString: string | null | undefined,
  fallback = "N/A",
): string {
  const datePart = dateString?.match(/^\d{4}-\d{2}-\d{2}/)?.[0]
  if (!datePart) {
    return fallback
  }

  try {
    const date = new Date(`${datePart}T00:00:00Z`)
    if (Number.isNaN(date.getTime())) {
      return fallback
    }

    return new Intl.DateTimeFormat("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      timeZone: "UTC",
    }).format(date)
  } catch {
    return fallback
  }
}

/**
 * Formats a date string as relative time (e.g., "9 days ago").
 * Returns a fallback value if the date is invalid or null.
 *
 * @param dateString - The date string to format (ISO 8601 format expected)
 * @param fallback - The fallback value if date is invalid (default: "N/A")
 * @returns The relative time string or fallback value
 */
export function formatRelativeTime(
  dateString: string | null | undefined,
  fallback = "N/A",
  baseDate?: string | Date | null,
): string {
  if (!dateString) {
    return fallback
  }

  try {
    const date = parseISO(dateString)
    if (!isValid(date)) {
      return fallback
    }

    const referenceDate =
      typeof baseDate === "string"
        ? parseISO(baseDate)
        : (baseDate ?? new Date())

    if (!isValid(referenceDate)) {
      return fallback
    }

    return formatDistanceStrict(date, referenceDate, { addSuffix: true })
  } catch {
    return fallback
  }
}
